import { connect as connectMqtt, Client } from 'mqtt';
import { BehaviorSubject, Subject, defer, filter, finalize, map } from 'rxjs';
import { MqttQos } from './models/mqtt-qos';
import { sleep } from '@weavix/utils/src/sleep';
import { MqttTransformationType } from './models/mqtt-transformation-type';
import { MqttStore } from './persistence/mqtt-store';
import { MqttMemoryStorage } from './persistence/mqtt-memory-storage';
import { TopicPayload } from './models/topic-payload';
import { TopicSubscription } from './models/topic-subscription';
import { timeout } from './utils/timeout';
import { MqttServiceOptions } from './models/mqtt-service-options';
import { Packet } from './models/packet';
import { ObservableSubscribe } from './models/observable-subscribe';

const REFRESH_TIME = 1_800_000; // Subscriptions error out and the client starts a fresh connection after this long

export class MqttService {

    public constructor(private readonly options: MqttServiceOptions) {
        this.storage = new MqttStore('outgoing', options.storage ?? new MqttMemoryStorage());
        if (!options.platform) options.platform = 'unknown';
        this.clientPrefix = `${this.options.platform}_${Math.random()}`;
    }

    /**
     * A queue of topics to batch subscribe/unsubscribe
     */
    private queued: Array<Packet> = [];
    /**
     * A list of topics currently being subscribed/unsubscribed
     */
    private transit: Array<Packet> = [];
    /**
     * Flag indicating whether subscribe/unsubscribe operation is in progress
     */
    private working = false;

    /**
     * Current connected mqtt client
     */
    private client?: Client | null;
    /**
     * Pending client in process of connecting, either due to disconnection or connection change
     */
    private pendingClient?: Client | null;
    /**
     * Promise for resolving when a connected client is available
     */
    private clientResolve?: (client: Client) => void;
    /**
     * Promise that resolves when a connected client is available
     */
    private clientPromise = new Promise<Client>(resolve => this.clientResolve = resolve);
    /**
     * Flag indicating whether the client has been torn down via disconnect()
     */
    private clientTornDown = false;

    /**
     * Current list of topics we are actively subscribed to.
     * Topics will be retained between reconnects.
     *
     * Only gets cleared on calls to disconnect() or connect().
     */
    private subscriptions: { [topic: string]: TopicSubscription<any> } = {};
    /**
     * Subject that emits incoming messages from subscribed topics
     */
    public message$: Subject<{ topic: string; payload: Buffer; dup: boolean; networkId?: number, networkType?: 'wifi' | 'mobile' }> = new Subject();

    /**
     * Indicates whether the active client is currently connected
     */
    public connected$ = new BehaviorSubject<boolean>(false);
    /**
     * When a client connects, emits MqttServiceOptions.networkId if provided or -1 otherwise
     * @platform Mobile - Simulcast
     */
    public connectedNetwork$ = new BehaviorSubject<number | null>(null);
    /**
     * An instance of `WebSocket`, depending on your platform.
     * When a client connects, emits the underlying socket object
     */
    public socket$ = new BehaviorSubject<any>(null);
    /**
     * The custom MqttStore used for persisting outgoing messages.
     *
     * Used to handle hung publish messages on reconnect.
     */
    private readonly storage: MqttStore;
    /**
     * Timestamp of the last packet received from the server.
     *
     * Used to detect stale connections.
     */
    private lastConnected?: number | null;
    /**
     * The clientId used for the current connection.
     *
     * Value of MqttServiceOptions.clientId if provided,
     * otherwise auto-generated based on MqttServiceOptions.platform and a random number.
     */
    public clientId?: string;
    /**
     * Used to generate unique clientIds on each connect().
     *
     * Generated from MqttServiceOptions.platform and a random number.
     */
    private readonly clientPrefix: string;
    /**
     * Number of times connect() has been called to generate unique clientIds.
     */
    private connectCount = 0;

    /**
     * The current reconnect interval in milliseconds.
     *
     * Used for exponential backoff when reconnecting.
     */
    private reconnectInterval = 1000;
    /**
     * Timeout interval for attempting to reconnect.
     */
    private reconnectTimeout?: ReturnType<typeof setTimeout>;

    /**
     * Timestamp of the last subscription refresh.
     *
     * Used to detect stale subscriptions.
     */
    private lastSubscriptionRefresh?: number | null;
    /**
     * Flag indicates whether subscriptions are being refreshed.
     */
    private refreshingSubscriptions = false;
    /**
     * Timeout interval for refreshing subscriptions.
     */
    private refreshTimeout?: ReturnType<typeof setTimeout>;

    private log(...args: unknown[]) {
        console.log(`MQTT ${this.options?.networkId ?? 'default'} ${this.socket$.value?._socketId ?? ''}:`, ...args);
    }

    private error(...args: unknown[]) {
        console.error(`MQTT ${this.options?.networkId ?? 'default'} ${this.socket$.value?._socketId ?? ''}:`, ...args);
    }

    private warn(...args: unknown[]) {
        console.warn(`MQTT ${this.options?.networkId ?? 'default'} ${this.socket$.value?._socketId ?? ''}:`, ...args);
    }

    /**
     * Client has been disconnected and is not attempting to reconnect.
     */
    get isClientTornDown() {
        return this.clientTornDown;
    }

    /**
     * Disconnects current connection if any, and establishes a new connection.
     * This should be called whenever a user logs in.
     * Clears all active subscriptions.
     *
     * @memberof MqttService
     */
    connect() {
        this.clientId = this.options.clientId ?? `${this.clientPrefix}_${this.connectCount++}`;
        this.disconnect(true);
        this.newConnection();
    }

    /**
     * Forces a reconnect of the current connection.
     * Re-subscribes to all active subscriptions.
     *
     * @memberof MqttService
     */
    reconnect() {
        this.log('force reconnect');
        this.newConnection();
    }

    /**
     * Establishes a new connection and sets up event listeners.
     */
    private newConnection() {
        this.log(`options: ${this.options.url} ${this.options.port} ${this.clientId} ${this.options.networkId}`);
        if (this.checkForStaleConnection()) {
            return;
        }
        this.clientTornDown = false;

        const port = Number(this.options.port ?? '443');
        const client = (this.options.connect ?? connectMqtt)({
            hostname: this.options.url,
            port,
            path: this.buildPath(),
            protocol: (port === 443 || this.options.ssl) ? 'wss' : 'ws',
            clean: false,
            keepalive: this.options.timeout ?? 240,
            clientId: this.clientId,
            username: '',
            password: this.options.token ?? '',
            reschedulePings: false,
            reconnectPeriod: 0,
            messageIdProvider: this.options.messageIdProvider
        });

        clearTimeout(this.reconnectTimeout);
        this.setupClientListeners(client);

        const previousPending = this.pendingClient;
        this.pendingClient = client;
        // make sure only one pending client
        previousPending?.end(true);

        this.log('done initializing');
    }

    /**
     * Sets up event listeners for the given MQTT client.
     */
    private setupClientListeners(client: Client) {
        // Generic event listener for logging
        ['connect', 'disconnect', 'error', 'close', 'end', 'offline', 'outgoingEmpty'].forEach(evt => {
            client.on(evt, (...args: unknown[]) => this.log(evt, ...args));
        });
        client.on('reconnect', () => this.log(`reconnect`, client.options));

        client.on('packetreceive', () => this.onPacketReceive(client));
        client.on('message', this.onMessage.bind(this));
        client.on('close', () => this.onClose(client));
        client.on('connect', () => this.onConnect(client));
    }

    /**
     * Fires when a packet is received from the server.
     * Used to check for stale connections and update lastConnected timestamp.
     * Handles erroneous packets that may be received from sleeping/waking computer.
     */
    private onPacketReceive(client: Client) {
        if (this.client !== client) return;

        // might receive erroneous packets from sleeping / waking computer, so catch that here
        if (this.checkForStaleConnection()) {
            return;
        }

        // Using packet receive since if there are no publish/subscribe messages, this gets ping acks
        this.lastConnected = Date.now();
    }

    /**
     * Fires when a message is received from the server.
     * Emits the message on the message$ subject.
     */
    private onMessage(topic: string, payload: Buffer, packet: any) {
        this.log(`Received message ${topic} ${payload.length}`);
        this.message$.next({ topic, payload, dup: packet.dup, networkId: this.options.networkId, networkType: this.options.network?.type });
    }

    /**
     * Fires when the client connection is closed.
     * Cleans up the current client and schedules a reconnect with exponential backoff.
     */
    private onClose(client: Client) {
        if (client !== this.client && client !== this.pendingClient) return;
        if (client === this.client) {
            this.client = null;
            this.connectedNetwork$.next(null);
            this.connected$.next(false);
            this.socket$.next(undefined);
            clearTimeout(this.refreshTimeout);
            if (this.pendingClient) return;
        } else if (client === this.pendingClient) {
            this.pendingClient = null;
        }
        this.reconnectTimeout = setTimeout(() => {
            this.reconnectInterval = Math.min(this.options.maxReconnectTimeout ?? 1000, this.reconnectInterval * 2);
            this.newConnection();
        }, this.reconnectInterval);
    }

    /**
     * Fires when the client successfully connects to the server.
     * Sets up the current client and refreshes subscriptions.
     */
    private onConnect(client: Client) {
        if (client !== this.pendingClient) return;

        const previousClient = this.client;
        this.pendingClient = null;
        this.client = client;
        if (this.clientResolve) this.clientResolve(client);
        this.clientPromise = new Promise(resolve => this.clientResolve = resolve);
        // make sure only one connected client (note the backend also closes the old connection due to matching clientId)
        previousClient?.end(true);

        this.reconnectInterval = 1000;
        this.log(`connected`);
        this.socket$.next((client as any).stream.socket);
        this.connectedNetwork$.next(this.options.networkId ?? -1);
        this.connected$.next(true);
        this.reset();
        if (!this.lastSubscriptionRefresh) this.lastSubscriptionRefresh = Date.now();
        const timeSinceRefresh = Date.now() - this.lastSubscriptionRefresh;
        clearTimeout(this.refreshTimeout);
        this.refreshTimeout = setTimeout(() => this.refreshSubscriptions(client), Math.max(1, REFRESH_TIME - timeSinceRefresh));
    }

    /**
     * Refreshes all active subscriptions by re-queuing them.
     */
    private refreshSubscriptions(client: Client) {
        if (client !== this.client) return;

        Object.keys(this.subscriptions).forEach(topic => {
            this.queued.push({ topic, qos: this.subscriptions[topic].qos, date: Date.now() });
        });
        this.refreshingSubscriptions = true;
        this.work();
        this.refreshTimeout = setTimeout(() => this.refreshSubscriptions(client), REFRESH_TIME);
    }

    /**
     * Builds the WebSocket path with query string parameters.
     */
    private buildPath(): string {
        const params = new URLSearchParams({
            source: this.options.source,
        });

        // We patched react-native to pull the networkId parameter and make the ws
        // connection over that network.
        if (this.options.networkId) params.append('networkId', this.options.networkId.toString());

        if (this.options.deviceId) params.append('deviceId', this.options.deviceId);

        this.addNetworkParams(params);

        return '/mqtt?' + params.toString();
    }

    /**
     * Adds network information from MqttServiceOptions.network to the given URLSearchParams.
     */
    private addNetworkParams(params: URLSearchParams) {
        try {
            if (!this.options.network?.type) return;

            const networkType = this.options.network.type;
            params.append('networkType', networkType);

            if (networkType === 'mobile') {
                if (this.options.network.carrier) params.append('carrier', this.options.network.carrier);
                if (this.options.network.imei) params.append('imei', this.options.network.imei);
                if (this.options.network.iccid) params.append('iccid', this.options.network.iccid);
                if (this.options.network.slot) params.append('slot', this.options.network.slot.toString());
            } else if (networkType === 'wifi') {
                if (this.options.network.ssid) params.append('ssid', this.options.network.ssid);
                if (this.options.network.mac) params.append('mac', this.options.network.mac);
                if (this.options.network.ip) params.append('ip', this.options.network.ip);
            }
        } catch (e) {
            this.error('Failed to add network info to query string parameters.', e);
        }
    }

    /**
     * Checks if the connection is stale based on lastConnected and lastSubscriptionRefresh timestamps.
     * If stale, initiates a reconnect.
     *
     * A stale connection is defined as no packets received for REFRESH_TIME,
     * or subscriptions not refreshed for nearly 2 * REFRESH_TIME.
     */
    private checkForStaleConnection() {
        if (this.lastConnected && Date.now() - this.lastConnected > REFRESH_TIME
            || this.lastSubscriptionRefresh && Date.now() - this.lastSubscriptionRefresh > 2 * REFRESH_TIME - 5 * 60_000) {
            this.connect();
            return true;
        }
        return false;
    }

    /**
     * Publishes data to the given topic
     * Use this method to stringify the data payload before publishing.
     *
     * @param {string} topic The resolved topic (mqtt wildcard characters should not be present)
     * @param {unknown} data The payload
     * @param {MqttQos} [qos=1] The delivery guarantee
     * @memberof MqttService
     */
    publish(topic: string, data: unknown, qos: MqttQos = 1, expirationMs?: number) {
        const stringified = typeof data === 'string' ? data : JSON.stringify(data || {});
        return this.publishRaw(topic, stringified, qos, expirationMs);
    }

    /**
     * Publishes data to the given topic
     * This method sends the raw data payload.
     *
     * @param {string} topic The resolved topic (mqtt wildcard characters should not be present)
     * @param {unknown} data The payload
     * @param {MqttQos} [qos=1] The delivery guarantee
     * @memberof MqttService
     */
    async publishRaw(topic: string, data: string | Buffer, qos: MqttQos = 1, expirationMs?: number) {
        this.log(`Publishing message ${topic}`, data.length);
        const messageId = `${Math.random()}`.substring(2);
        const packet = { messageId, topic, qos, payload: data };
        if (qos > 0) this.storage.put(packet as any, expirationMs);
        return this.publishImpl(messageId, topic, data, qos);
    }

    /**
     * Publishes the given data to the specified topic.
     */
    private async publishImpl(messageId: string, topic: string, data: string | Buffer, qos: MqttQos = 1, dup = false) {
        if (!this.connected$.value) return;

        const client = await this.waitForClient();
        const promise = new Promise((resolve, reject) => {
            client.publish(topic, data, { qos, dup }, (err) => {
                if (err) {
                    reject(err);
                    this.error(err);
                    return;
                }
                if (qos > 0) this.storage.del({ messageId } as any);
                resolve(null);
            });
        });
        const timeoutMessage = `Publish timeout ${messageId} ${topic} ${data}`;
        await Promise.race([timeout(timeoutMessage), promise]);
    }

    /**
     * Subscribes to the given topic.
     *
     * @template T The type of payload that will be delivered on this topic
     * @param {string} topic The resolved topic (mqtt wildcard characters should not be present)
     * @param {MqttQos} [qos=1] The receipt guarantee
     * @return {*}  {ObservableSubscribe<TopicPayload<T>>}
     * @memberof MqttService
     */
    subscribe<T>(topic: string, qos: MqttQos = 1, transformation: MqttTransformationType = MqttTransformationType.json): ObservableSubscribe<TopicPayload<T>> {
        let observable: ObservableSubscribe<TopicPayload<T>> = this.subscriptions[topic]?.observable;
        if (!observable) {
            const regex = new RegExp(`^${topic
                .replaceAll('.', String.raw`\.`).replaceAll('(', String.raw`\(`).replaceAll(')', String.raw`\)`).replaceAll('*', String.raw`\*`)
                .replaceAll('#', '(.*)').replaceAll(/(\^|\+)/g, '([^/]+)')}$`);
            let count = 0;
            observable = defer(() => {
                if (count === 0 && this.subscriptions[topic]?.observable !== observable) {
                    // if we unsubscribe and resubscribe right away, need to re-initialize the observable
                    init();
                }
                count++;
                return this.message$.pipe(map(v => {
                    const replacements = v.topic.match(regex);
                    if (!replacements) return;
                    const payload = transformation === MqttTransformationType.json ? JSON.parse(v.payload.toString('utf8')) : v.payload;
                    try {
                        return { replacements, topic: v.topic, payload, dup: v.dup, networkId: v.networkId, networkType: v.networkType };
                    } catch (e) {
                        this.error(e);
                    }
                }), filter(v => !!v), finalize(() => {
                    count--;
                    if (count <= 0 && this.subscriptions[topic]?.observable === observable) {
                        delete this.subscriptions[topic];
                        this.queued.push({ topic, qos: -1, date: Date.now() });
                        this.work();
                    }
                }));
            }) as unknown as ObservableSubscribe<TopicPayload<T>>;
            let resolve: (value: boolean) => void;
            let reject: (reason?: unknown) => void;
            observable.subscribed = new Promise((res, rej) => {
                resolve = res;
                reject = rej;
            });
            const init = () => {
                this.subscriptions[topic] = {
                    observable,
                    qos,
                    resolve,
                    reject
                };
                this.queued.push({ topic, qos, date: Date.now() });
                this.work();
            };
            init();
        }
        return observable;
    }

    /**
     * Disconnects the current client and clears all active subscriptions.
     *
     * @memberof MqttService
     */
    disconnect(fromConnect = false) {
        this.log(`shutting down client`);
        const client = this.client;
        const pendingClient = this.pendingClient;
        const subscriptions = this.subscriptions;
        const message$ = this.message$;
        this.client = null;
        this.pendingClient = null;
        this.lastConnected = null;
        this.lastSubscriptionRefresh = null;
        this.refreshingSubscriptions = false;
        this.message$ = new Subject();
        this.subscriptions = {};
        this.queued = [];
        this.transit = [];
        client?.end(true);
        pendingClient?.end(true);
        this.connectedNetwork$.next(null);
        this.connected$.next(false);
        this.socket$.next(undefined);
        clearTimeout(this.refreshTimeout);
        clearTimeout(this.reconnectTimeout);
        if (!fromConnect) this.clientTornDown = true;
        Object.values(subscriptions).forEach(x => {
            x.reject?.('mqtt client closed');
        });
        message$.error(new Error('disconnected')); // Run last so old client stuff is not picked up
    }

    /**
     * Clears all persisted outgoing messages.
     *
     * @memberof MqttService
     */
    clearStorage() {
        this.storage.clear();
    }

    /**
     * Handles retrying of subscribe/unsubscribe operations and sending persisted messages.
     */
    private async reset() {
        // Anything in transit goes back to the front of the queue, other queued stuff still remains
        this.queued = [...this.transit, ...this.queued];
        this.transit = [];

        // Send any unconfirmed publishes
        const stream = await this.storage.createStream();
        while (true) {
            const packet = stream.read(1);
            if (!packet) break;
            this.publishImpl(packet.messageId, packet.topic, packet.payload, packet.qos, true);
        }
        this.work(true);
    }

    /**
     * Processes the queued subscribe/unsubscribe operations in batches.
     * @param {boolean} [immediate=false] Whether to process immediately without delay.
     */
    private async work(immediate = false) {
        if (this.working) return;
        this.working = true;

        try {
            if (!this.connected$.value) return; // Don't send anything til connected
            if (!immediate) await sleep(50); // Give some time to batch subscription requests

            const client = await this.waitForClient();
            const subs: { [topic: string]: { qos: MqttQos } } = {};
            const unsubs: { [topic: string]: -1 } = {};
            const transit = this.queued;
            this.queued = [];
            transit.forEach(x => {
                if (x.qos === -1) {
                    delete subs[x.topic];
                    unsubs[x.topic] = x.qos;
                } else {
                    delete unsubs[x.topic];
                    subs[x.topic] = { qos: x.qos };
                }
            });
            this.transit.push(...transit);
            if (Object.keys(subs).length) {
                client.subscribe(subs, (err, granted) => {
                    if (err) {
                        return this.error(err);
                    }

                    if (client !== this.client) return;

                    if (this.checkForStaleConnection()) {
                        return;
                    }

                    if (this.refreshingSubscriptions) {
                        this.refreshingSubscriptions = false;
                        this.lastSubscriptionRefresh = Date.now();
                    }

                    // Only remove transit added from here (future transit could have unsubbed the same topic)
                    this.transit = this.transit.filter(x => !transit.includes(x) || !subs[x.topic]);

                    this.log(`Subscribed to ${Object.keys(subs).join(', ')}`);
                    granted?.forEach(x => {
                        const topicAuthorized = x.qos !== 128;
                        if (!topicAuthorized) this.warn(`Subscribe not authorized to ${x.topic}`);
                        this.subscriptions[x.topic]?.resolve?.(topicAuthorized);
                    });
                });
            }
            if (Object.keys(unsubs).length) {
                client.unsubscribe(Object.keys(unsubs), (err?: Error) => {
                    if (err) {
                        return this.error(err);
                    }

                    if (client !== this.client) return;

                    // Only remove transit added from here (future transit could have subbed the same topic);
                    this.transit = this.transit.filter(x => !transit.includes(x) || !unsubs[x.topic]);

                    this.log(`Unsubscribed from ${Object.keys(unsubs).join(', ')}`);
                });
            }
        } catch (e) {
            this.error(e);
        } finally {
            this.working = false;
        }
    }

    /**
     * Waits for a connected MQTT client to be available.
     */
    private async waitForClient(): Promise<Client> {
        /*
            wait a few seconds to cover out-of-order things during startup.
            buf if it takes too long, maybe we've logged out or something
        */
        if (this.client) return this.client;
        return Promise.race([
            timeout('client startup timeout'),
            this.clientPromise
        ]);
    }
}
